/**
 * Featured Products Slider Handler
 * Conditionally initializes Slick slider based on block count and screen size
 */
class FeaturedProductsSlider {
  constructor(containerSelector = '.section-featured-products .collection-list-slider') {
    this.containerSelector = containerSelector;
    this.sliderButtonsSelector = '.section-featured-products .slider-buttons';
    this.counterCurrentSelector = '.section-featured-products .slider-buttons .slider-counter--current';
    this.counterTotalSelector = '.section-featured-products .slider-buttons .slider-counter--total';
    this.prevButtonSelector = '.section-featured-products .slider-buttons .slider-button--prev';
    this.nextButtonSelector = '.section-featured-products .slider-buttons .slider-button--next';
    this.sliderInitialized = false;
    this.resizeTimer = null;
    this.totalBlocks = 0;
    
    this.init();
  }

  /**
   * Initialize the slider handler
   */
  init() {
    try {
      // Check if jQuery is available
      if (typeof $ === 'undefined') {
        console.warn('FeaturedProductsSlider: jQuery is not available');
        return;
      }

      // Check if container exists
      const $container = $(this.containerSelector);
      if (!$container.length) {
        return; // Silently return if container doesn't exist
      }

      // Get total blocks count from data attribute or child elements
      this.totalBlocks = this.getTotalBlocks($container);
      
      if (this.totalBlocks === 0) {
        console.warn('FeaturedProductsSlider: No blocks found');
        return;
      }

      // Initialize slider logic
      this.setupSlider();
      
    } catch (error) {
      console.error('FeaturedProductsSlider initialization failed:', error);
    }
  }

  /**
   * Get total number of blocks
   */
  getTotalBlocks($container) {
    try {
      // Try to get from data attribute first
      const dataBlocks = $container.data('total-blocks');
      if (dataBlocks && dataBlocks > 0) {
        return parseInt(dataBlocks, 10);
      }

      // Fallback to counting child elements
      const childCount = $container.children('li').length;
      return childCount;
    } catch (error) {
      console.error('Error getting total blocks:', error);
      return 0;
    }
  }

  /**
   * Setup slider functionality
   */
  setupSlider() {
    try {
      // Initially hide slider buttons
      $(this.sliderButtonsSelector).hide();

      // Setup custom navigation handlers
      this.setupCustomNavigation();

      // Initial slider logic check
      this.handleSliderLogic();

      // Setup resize handler with debounce
      this.setupResizeHandler();

    } catch (error) {
      console.error('Error setting up slider:', error);
    }
  }

  /**
   * Setup custom navigation handlers
   */
  setupCustomNavigation() {
    try {
      const self = this;

      // Remove any existing event handlers to prevent duplicates
      $(this.prevButtonSelector).off('click.featuredProductsSlider');
      $(this.nextButtonSelector).off('click.featuredProductsSlider');

      // Setup custom navigation
      $(this.prevButtonSelector).on('click.featuredProductsSlider', function(e) {
        e.preventDefault();
        if (self.sliderInitialized) {
          $(self.containerSelector).slick('slickPrev');
        }
      });

      $(this.nextButtonSelector).on('click.featuredProductsSlider', function(e) {
        e.preventDefault();
        if (self.sliderInitialized) {
          $(self.containerSelector).slick('slickNext');
        }
      });

    } catch (error) {
      console.error('Error setting up custom navigation:', error);
    }
  }

  /**
   * Get current slides to show based on screen size
   */
  getCurrentSlidesToShow() {
    try {
      const windowWidth = $(window).width();
      
      if (windowWidth >= 990) {
        return Math.min(4, this.totalBlocks);
      } else if (windowWidth >= 600) {
        return Math.min(2, this.totalBlocks);
      } else {
        return 1;
      }
    } catch (error) {
      console.error('Error getting current slides to show:', error);
      return 1;
    }
  }

  /**
   * Update slider counter
   */
  updateCounter(slick) {
    try {
      if (!slick) return;

      const currentSlide = slick.currentSlide || 0;
      const slidesToShow = this.getCurrentSlidesToShow();
      const slidesCount = slick.slideCount || this.totalBlocks;
      
      const current = Math.ceil((currentSlide + 1) / slidesToShow);
      const total = Math.ceil(slidesCount / slidesToShow);

      // Update counter display
      $(this.counterCurrentSelector).text(current);
      $(this.counterTotalSelector).text(total);

    } catch (error) {
      console.error('Error updating counter:', error);
    }
  }

  /**
   * Setup slider event handlers
   */
  setupSliderEvents() {
    try {
      const self = this;
      const $container = $(this.containerSelector);

      // Remove existing event handlers to prevent duplicates
      $container.off('.featuredProductsSlider');

      // Handle slider initialization
      $container.on('init.featuredProductsSlider', function(event, slick) {
        setTimeout(() => {
          self.updateCounter(slick);
        }, 0);
      });

      // Handle slide changes
      $container.on('afterChange.featuredProductsSlider', function(event, slick, currentSlide) {
        self.updateCounter(slick);
      });

      // Handle responsive breakpoint changes
      $container.on('breakpoint.featuredProductsSlider', function(event, slick, breakpoint) {
        setTimeout(() => {
          self.updateCounter(slick);
        }, 100);
      });

    } catch (error) {
      console.error('Error setting up slider events:', error);
    }
  }

  /**
   * Determine if slider should be initialized based on screen size and block count
   */
  shouldInitializeSlider() {
    try {
      const windowWidth = $(window).width();

      if (windowWidth >= 990) {
        // Desktop: Initialize only if more than 4 blocks
        return this.totalBlocks > 4;
      } else {
        // Mobile: Initialize if 2 or more blocks (not if only 1 block)
        return this.totalBlocks >= 2;
      }
    } catch (error) {
      console.error('Error checking slider initialization condition:', error);
      return false;
    }
  }

  /**
   * Initialize Slick slider
   */
  initializeSlider() {
    if (this.sliderInitialized) return;

    try {
      // Check if Slick is available
      if (typeof $.fn.slick === 'undefined') {
        console.error('FeaturedProductsSlider: Slick slider is not available');
        return;
      }

      const $container = $(this.containerSelector);
      if (!$container.length) return;

      // Setup slider events before initialization
      this.setupSliderEvents();

      // Initialize Slick slider with arrows disabled (using custom navigation)
      $container.slick({
        slidesToShow: 4,
        slidesToScroll: 4,
        autoplay: true,
        autoplaySpeed: 7000,
        infinite: false,
        arrows: false, // Disable default arrows, use custom navigation
        responsive: [
          {
            breakpoint: 990,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
              arrows: false
            }
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false
            }
          }
        ]
      });

      // Show slider controls
      $(this.sliderButtonsSelector).show();
      this.sliderInitialized = true;

      // Initial counter update
      setTimeout(() => {
        const slickInstance = $container.slick('getSlick');
        this.updateCounter(slickInstance);
      }, 0);

    } catch (error) {
      console.error('Error initializing slider:', error);
    }
  }

  /**
   * Destroy Slick slider
   */
  destroySlider() {
    if (!this.sliderInitialized) return;

    try {
      const $container = $(this.containerSelector);
      
      // Remove event handlers
      $container.off('.featuredProductsSlider');
      
      if ($container.hasClass('slick-initialized')) {
        $container.slick('unslick');
      }

      // Hide slider controls
      $(this.sliderButtonsSelector).hide();
      
      // Reset counter to initial state
      $(this.counterCurrentSelector).text('1');
      $(this.counterTotalSelector).text(this.totalBlocks);
      
      this.sliderInitialized = false;

    } catch (error) {
      console.error('Error destroying slider:', error);
    }
  }

  /**
   * Handle slider logic - initialize or destroy based on conditions
   */
  handleSliderLogic() {
    try {
      if (this.shouldInitializeSlider()) {
        this.initializeSlider();
      } else {
        this.destroySlider();
      }
    } catch (error) {
      console.error('Error handling slider logic:', error);
    }
  }

  /**
   * Setup resize handler with debounce
   */
  setupResizeHandler() {
    try {
      const self = this;
      
      $(window).on('resize.featuredProductsSlider', function() {
        clearTimeout(self.resizeTimer);
        self.resizeTimer = setTimeout(function() {
          self.destroySlider(); // Always destroy first
          self.handleSliderLogic(); // Then reinitialize if needed
        }, 250);
      });

    } catch (error) {
      console.error('Error setting up resize handler:', error);
    }
  }

  /**
   * Destroy instance and cleanup
   */
  destroy() {
    try {
      this.destroySlider();
      $(window).off('resize.featuredProductsSlider');
      $(this.prevButtonSelector).off('click.featuredProductsSlider');
      $(this.nextButtonSelector).off('click.featuredProductsSlider');
      clearTimeout(this.resizeTimer);
    } catch (error) {
      console.error('Error destroying FeaturedProductsSlider instance:', error);
    }
  }
}

/**
 * Initialize Featured Products Slider when DOM is ready
 */
$(document).ready(function() {
  // Initialize slider for featured products section
  window.featuredProductsSlider = new FeaturedProductsSlider('.collection-list-slider');
});